import { Box, Text } from '@chakra-ui/react'
import { Hanko, register } from '@teamhanko/hanko-elements'
import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import type { ComponentProps, ComponentType } from 'react'

import { dropSession } from './cookie'
import type StandIn from './dev/DevSignIn'

/** Where a production build would have the stand-in: nothing. */
function NoStandIn(_: ComponentProps<typeof StandIn>) {
  return null
}

/**
 * The development stand-in for the boundary's interface, where this is a
 * development build.
 *
 * The condition is the literal `import.meta.env.DEV` on purpose. Vite replaces
 * it with `false` in a production build, the import is dead, and the chunk is
 * never emitted — so a production bundle does not merely hide the stand-in, it
 * does not contain it. Read through a helper, the value would not be folded and
 * the import would survive. The frontend's `pnpm build` fails if it ever does.
 *
 * Decided when the component first loads rather than when this module does. A
 * build's DEV never changes, but a test's does, and a module first loaded while
 * a test said "production" would otherwise have no stand-in for the rest of
 * the file.
 */
const DevSignIn = lazy<ComponentType<ComponentProps<typeof StandIn>>>(() =>
  import.meta.env.DEV ? import('./dev/DevSignIn') : Promise.resolve({ default: NoStandIn }),
)

/**
 * Where the authentication boundary lives.
 *
 * Read per call rather than captured at module load: a value frozen by import
 * order is a value a test cannot set and an operator cannot change.
 */
function hankoAPI(): string {
  return import.meta.env.VITE_HANKO_API_URL ?? ''
}

/** Whether an authentication boundary is configured at all. */
function boundaryConfigured(): boolean {
  return hankoAPI() !== ''
}

/**
 * A client for the boundary, or undefined where none is configured.
 *
 * Built per call for the same reason the URL is read per call: a client frozen
 * at module load is one a test cannot replace. Callers that need it across
 * renders should memoise it themselves.
 */
function boundaryClient(): Hanko | undefined {
  return boundaryConfigured() ? new Hanko(hankoAPI()) : undefined
}

/**
 * Whether the development stand-in takes the boundary's place.
 *
 * Only in a development build, and only where no boundary is configured: where
 * one is, it is presented, development or not, and the two are never offered
 * side by side. Read per call, like the URL, so a test can say which build it
 * is in.
 */
function standInOffered(): boolean {
  return import.meta.env.DEV && !boundaryConfigured()
}

/**
 * Whether anyone can sign in here at all — through the boundary, or through
 * the stand-in in its place, which a development build always has to offer.
 *
 * This is the question every "is there a way in, is there a way out" check is
 * asking, and the one the applications should ask.
 */
export function signInOffered(): boolean {
  return import.meta.env.DEV || boundaryConfigured()
}

/**
 * Ends the session wherever it was begun.
 *
 * The boundary's own logout where there is a boundary. Otherwise the stand-in's
 * session is nothing but the cookie it set, so expiring that is the whole of
 * leaving — and the admin, which has no route of its own to do it, depends on
 * this doing it. Rejects only if the boundary does.
 */
export async function leaveBoundary(): Promise<void> {
  const hanko = boundaryClient()
  if (hanko !== undefined) {
    await hanko.logout()
    return
  }

  if (standInOffered()) dropSession()
}

interface HankoAuthProps {
  /**
   * Called with the session token once the boundary has authenticated someone.
   *
   * The token is passed explicitly rather than left to the cookie: whether the
   * boundary sets its cookie on Scout's domain is a project setting Scout does
   * not control, and a sign-in that silently fails to reach the backend is
   * worse than one that never started.
   */
  onSignedIn: (sessionToken: string) => void

  /**
   * Which of the boundary's flows the form opens on.
   *
   * `sign-in` is the boundary's combined element, which starts at logging in
   * and links across to registering. `sign-up` starts at registering and
   * offers nothing else, for a caller that has just been asked to create an
   * account. Starting that caller on the login flow put a new address into
   * logging in, which the boundary refuses as a flow it cannot continue rather
   * than moving on to registering.
   */
  start?: 'sign-in' | 'sign-up'
}

/**
 * The boundary's own sign-in interface.
 *
 * Scout renders none of it. Which methods are offered — a passkey, a password,
 * a federated account, an enterprise directory — is the boundary's decision,
 * and deliberately not one Scout participates in or can observe.
 */
export function HankoAuth({ onSignedIn, start = 'sign-in' }: HankoAuthProps) {
  const [failed, setFailed] = useState(false)

  const hanko = useMemo(() => boundaryClient(), [])

  useEffect(() => {
    if (!boundaryConfigured()) return

    register(hankoAPI()).catch(() => {
      // The element never registers, so there is nothing to sign in with.
      // Said out loud rather than leaving an empty box on the page.
      setFailed(true)
    })
  }, [])

  useEffect(() => {
    // No boundary configured, so nothing to listen to. The cleanup is a no-op
    // rather than absent, so every path returns the same shape.
    if (hanko === undefined) return () => undefined

    return hanko.onSessionCreated(() => {
      const token = hanko.getSessionToken()
      if (token !== '') onSignedIn(token)
    })
  }, [hanko, onSignedIn])

  // The stand-in takes the boundary's place, with the same callback and the
  // same flow, so nothing that renders this needs to know which it got.
  if (standInOffered()) {
    return (
      <Suspense fallback={null}>
        <DevSignIn onSignedIn={onSignedIn} start={start} />
      </Suspense>
    )
  }

  if (!boundaryConfigured()) return null

  if (failed) {
    return (
      <Text role="alert" fontSize="sm" color="rust.fg">
        Couldn&rsquo;t reach the sign-in service. Try again in a moment.
      </Text>
    )
  }

  return (
    <Box css={boundaryTheme}>{start === 'sign-up' ? <hanko-registration /> : <hanko-auth />}</Box>
  )
}

/**
 * Scout's palette, handed to the boundary's own interface.
 *
 * The boundary renders its form in a shadow root, so none of Scout's CSS
 * reaches inside it. What does reach inside is custom properties, which inherit
 * through the shadow boundary — and the element reads its whole appearance from
 * a documented set of them. Setting them here is the only way to dress it, and
 * notably not a way to change it: which methods are offered, what the form
 * says, and what it does are all still the boundary's, exactly as before.
 *
 * Every value is a Chakra variable rather than a literal, so this moves with
 * the theme instead of drifting from it. Raw hex here would be the one place in
 * the application that stopped matching the day the palette changed.
 *
 * Left alone deliberately: `--font-size` and `--font-weight`. The defaults are
 * 16px/400, which is what Scout's base already is, and a smaller value would
 * cost the inputs — iOS zooms the page on any input under 16px.
 */
const boundaryTheme = {
  // Transparent, not `bg.panel`. This is rendered in two places — a dialog and
  // a bordered card — and the default white drew a rectangle inside both. Let
  // it take the surface it is placed on.
  '--background-color': 'transparent',
  // The host already provides padding and width; the element's own would be a
  // second inset box inside the first.
  '--container-padding': '0',
  '--container-max-width': '100%',

  '--color': 'var(--chakra-colors-fg)',
  '--color-shade-1': 'var(--chakra-colors-fg-muted)',
  // Borders, dividers and disabled edges all read this one.
  '--color-shade-2': 'var(--chakra-colors-border)',

  '--brand-color': 'var(--chakra-colors-forest-solid)',
  '--brand-color-shade-1': 'var(--chakra-colors-forest-fg)',
  '--brand-contrast-color': 'var(--chakra-colors-forest-contrast)',

  // The one warm hue, and already what every other error in Scout wears.
  '--error-color': 'var(--chakra-colors-rust-fg)',

  // Underlined rather than coloured-and-bare: with the blue gone, the
  // underline is what is left to say a link is one.
  '--link-color': 'var(--chakra-colors-forest-fg)',
  '--link-text-decoration': 'underline',

  '--font-family': 'var(--chakra-fonts-body)',
  '--line-height': 'var(--chakra-line-heights-body)',
  '--border-radius': 'var(--chakra-radii-l2)',
  // Chakra's medium control height, so the boundary's inputs and buttons line
  // up with Scout's either side of them.
  '--item-height': '2.5rem',

  // `md`, not something larger, because this heading has two homes: alone in
  // the sign-in dialog, and inside the sign-up card beneath "Keep this brief".
  // A size that carries the dialog on its own overpowers the card it sits in.
  '--headline1-font-size': 'var(--chakra-font-sizes-md)',
  '--headline1-font-weight': '600',
  '--headline2-font-size': 'var(--chakra-font-sizes-sm)',
  '--headline2-font-weight': '600',
}
